"use client"

/**
 * Типы документов из backend DocumentTypeRegistry (Wave 0).
 *
 * Fetch один раз на сессию кабинета; до ответа / при ошибке — bootstrap-зеркало
 * реестра (те же коды/capabilityId), чтобы sync-предикаты CAPABILITIES и тесты
 * не зависели от сети. Источник правды после hydrate — API.
 *
 * @see docs/plan/document-lifecycle-architecture.md §7
 */

export interface DocumentTypeDefinition {
  code: string
  label: string
  direction: "outgoing" | "incoming" | "both"
  family: "dispute" | "contract" | "court"
  /** @internal Wave 4 — court-types; в контракте с Wave 0. */
  addressee: "counterparty" | "court" | "authority" | "other"
  capabilityId: string | null
  registryType: string
  draftable: boolean
  /** Документ ожидает денежные claimItems (money-канал). */
  hasMoneyChannel: boolean
}

/**
 * Bootstrap до hydrate с API. Должен совпадать с
 * `App\Service\AI\Document\DocumentTypeRegistry` — иначе карточки «Главной»
 * разъедутся до первого fetch.
 */
export const BOOTSTRAP_DOCUMENT_TYPES: readonly DocumentTypeDefinition[] = [
  {
    code: "claim_outgoing",
    label: "Претензия",
    direction: "outgoing",
    family: "dispute",
    addressee: "counterparty",
    capabilityId: "draftClaim",
    registryType: "claim_outgoing",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "claim_response",
    label: "Ответ на претензию",
    direction: "outgoing",
    family: "dispute",
    addressee: "counterparty",
    capabilityId: "draftResponse",
    registryType: "correspondence",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "notice",
    label: "Уведомление",
    direction: "outgoing",
    family: "contract",
    addressee: "counterparty",
    capabilityId: "draftContractDoc",
    registryType: "notice",
    draftable: true,
    hasMoneyChannel: false,
  },
  {
    code: "letter",
    label: "Письмо",
    direction: "outgoing",
    family: "contract",
    addressee: "counterparty",
    capabilityId: "draftContractDoc",
    registryType: "correspondence",
    draftable: true,
    hasMoneyChannel: false,
  },
  {
    code: "agreement",
    label: "Дополнительное соглашение",
    direction: "outgoing",
    family: "contract",
    addressee: "counterparty",
    capabilityId: "draftContractDoc",
    registryType: "supplement",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "claim_disagreement",
    label: "Протокол разногласий",
    direction: "outgoing",
    family: "contract",
    addressee: "counterparty",
    capabilityId: "draftContractDoc",
    registryType: "supplement",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "lawsuit",
    label: "Исковое заявление",
    direction: "outgoing",
    family: "court",
    addressee: "court",
    capabilityId: "draftLawsuit",
    registryType: "court_document",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "court_response",
    label: "Отзыв на исковое заявление",
    direction: "outgoing",
    family: "court",
    addressee: "court",
    capabilityId: "draftCourtDoc",
    registryType: "court_document",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "motion",
    label: "Ходатайство",
    direction: "outgoing",
    family: "court",
    addressee: "court",
    capabilityId: "draftCourtDoc",
    registryType: "court_document",
    draftable: true,
    hasMoneyChannel: false,
  },
  {
    code: "claim_amendment",
    label: "Уточнение исковых требований",
    direction: "outgoing",
    family: "court",
    addressee: "court",
    capabilityId: "draftCourtDoc",
    registryType: "court_document",
    draftable: true,
    hasMoneyChannel: true,
  },
  {
    code: "other",
    label: "Документ",
    direction: "both",
    family: "dispute",
    addressee: "other",
    capabilityId: null,
    registryType: "other",
    draftable: true,
    hasMoneyChannel: false,
  },
]

let sessionCache: DocumentTypeDefinition[] | null = null
let pending: Promise<DocumentTypeDefinition[]> | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const cb of listeners) cb()
}

/** Текущий список: session-cache после API, иначе bootstrap. */
export function getDocumentTypes(): DocumentTypeDefinition[] {
  return sessionCache ?? [...BOOTSTRAP_DOCUMENT_TYPES]
}

export function isDocumentTypesHydrated(): boolean {
  return sessionCache !== null
}

export function subscribeDocumentTypes(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Подмена для unit-тестов. */
export function setDocumentTypesForTests(types: DocumentTypeDefinition[] | null): void {
  sessionCache = types
  pending = null
  notify()
}

/** Громкий сигнал при расхождении bootstrap-зеркала с реестром backend (W1 критика). */
function warnOnBootstrapDrift(api: DocumentTypeDefinition[]): void {
  const bootCodes = BOOTSTRAP_DOCUMENT_TYPES.map((t) => t.code).sort().join(",")
  const apiCodes = api.map((t) => t.code).sort().join(",")
  if (bootCodes !== apiCodes) {
    console.warn(
      "[document-types] bootstrap расходится с API — обнови BOOTSTRAP_DOCUMENT_TYPES в lib/lower/document-types.ts",
      { bootstrap: bootCodes, api: apiCodes },
    )
  }
}

export function codesForCapability(capabilityId: string): string[] {
  return getDocumentTypes()
    .filter((t) => t.draftable && t.capabilityId === capabilityId)
    .map((t) => t.code)
}

export function hasDraftForCapability(
  draftTypes: ReadonlySet<string>,
  capabilityId: string,
): boolean {
  return codesForCapability(capabilityId).some((code) => draftTypes.has(code))
}

export function labelForDocumentType(code: string): string {
  return getDocumentTypes().find((t) => t.code === code)?.label ?? "Документ"
}

/**
 * Fetch один раз на сессию. Повторные вызовы возвращают кэш / in-flight promise.
 */
export async function ensureDocumentTypesLoaded(): Promise<DocumentTypeDefinition[]> {
  if (sessionCache) return sessionCache
  if (pending) return pending

  // Динамический import: api.ts тянет lower-auth-store (useEffect) — нельзя
  // статически импортировать из Server Components (landing app/page.tsx).
  pending = import("@/lib/lower/api")
    .then(({ lowerApi }) => lowerApi.get<DocumentTypeDefinition[]>("/api/lower/document-types"))
    .then((data) => {
      sessionCache = Array.isArray(data) ? data : [...BOOTSTRAP_DOCUMENT_TYPES]
      pending = null
      if (Array.isArray(data)) warnOnBootstrapDrift(data)
      notify()
      return sessionCache
    })
    .catch((err) => {
      pending = null
      // Bootstrap остаётся — карточки не пустеют при сетевом сбое.
      console.warn("[document-types] fetch failed, using bootstrap", err)
      return getDocumentTypes()
    })

  return pending
}
